<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="1100px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onAbort"
        @click:outside="onAbort"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Dane notki
                </h1>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-card-text>
                        <v-container>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="note.title"
                                        label="Tytuł"
                                        outlined
                                        dense
                                        required
                                        :readonly="!isNewNote"
                                        :error-messages="titleErrors"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="note.text"
                                        label="Opis"
                                        outlined
                                        dense
                                        auto-grow
                                        required
                                        :readonly="!isNewNote"
                                        :error-messages="textErrors"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-if="!isNewNote"
                                        :value="userData"
                                        label="Autor"
                                        outlined
                                        dense
                                        readonly
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col v-if="!isNewNote" cols="12">
                                    <v-textarea
                                        v-model="comment.comment"
                                        label="Dodaj komentarz"
                                        :disabled="!canUserCommentNote"
                                        auto-grow
                                        clearable
                                        hide-details
                                    />
                                    <v-row justify="end">
                                        <v-col>
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Wyślij komentarz"
                                                :disabled="comment.comment === null || comment.comment === '' || !canUserCommentNote"
                                                :loading="actionLoading.addComment"
                                                @click="addComment"
                                            >
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-data-table
                                        v-if="!isNewNote"
                                        :headers="commentsHeaders"
                                        :items="note.productionSiteNoteComments"
                                        :mobile-breakpoint="0"
                                    >
                                        <template #top>
                                            <v-divider />
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Komentarze</v-toolbar-title>
                                            </v-toolbar>
                                        </template>
                                        <template #item.createdBy="{item}">
                                            <template v-if="item.createdBy && item.createdBy.firstName && item.createdBy.lastName">
                                                {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
                                            </template>
                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                        <template #item.createdAt="{item}">
                                            {{ formatDate(item.createdAt) }}
                                        </template>
                                        <template #item.comment="{item}">
                                            {{ item.comment }}
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-row>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onAbort"
                >
                    Zamknij
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isNewNote"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading.saveNote"
                    @click="createNote"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import { required } from 'vuelidate/lib/validators'

export default {
    name: 'SingleNoteDialog',
    mixins: [
        dateFormatter,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        note: {
            type: Object,
            default: null
        },
        comment: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            actionLoading: {
                saveNote: false,
                addComment: false
            },
            commentsHeaders: [
                {
                    text: 'Pracownik',
                    sortable: false,
                    value: 'createdBy',

                },
                {
                    text: 'Data utworzenia',
                    sortable: false,
                    value: 'createdAt',
                },
                {
                    text: 'Treść',
                    sortable: false,
                    value: 'comment'
                },
            ],
        }
    },
    validations() {
        return {
            note: {
                title: {
                    required
                },
                text: {
                    required
                },
            }
        }
    },
    computed: {
        isNewNote() {
            return !this.note.id
        },
        canUserCommentNote() {
            return this.note.canComment && this.note.approved // 1 komentarz/notkę + status zaakceptowana
        },
        userData() {
            const user = this.note.createdBy
            
            if (user) {
                
                return `${user.firstName} ${user.lastName}`  
            }

            return '-'
        },
        titleErrors() {
            const errors = []

            if (!this.$v.note.title.$dirty) return errors
            !this.$v.note.title.required && errors.push('Proszę podać tytuł notki.')

            return errors
        },
        textErrors() {
            const errors = []

            if (!this.$v.note.text.$dirty) return errors
            !this.$v.note.text.required && errors.push('Proszę podać opis notki.')

            return errors
        },
    },
    methods: {
        onAbort() {
            this.$emit('close-single-note-dialog')
            this.$v.$reset()
        },
        createNote() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.$emit('create-single-note')
                this.$v.$reset()
            }
        },
        addComment() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.$emit('create-single-note-comment')
                this.$v.$reset()
            }
        }
    }
}
</script>